import { OASElement, escapeText } from '@oas-ui/core'

/** 希腊字母映射（LaTeX 命令 → Unicode 字符） */
const GREEK: Record<string, string> = {
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  epsilon: 'ε',
  zeta: 'ζ',
  eta: 'η',
  theta: 'θ',
  iota: 'ι',
  kappa: 'κ',
  lambda: 'λ',
  mu: 'μ',
  nu: 'ν',
  xi: 'ξ',
  pi: 'π',
  rho: 'ρ',
  sigma: 'σ',
  tau: 'τ',
  upsilon: 'υ',
  phi: 'φ',
  chi: 'χ',
  psi: 'ψ',
  omega: 'ω',
  Gamma: 'Γ',
  Delta: 'Δ',
  Theta: 'Θ',
  Lambda: 'Λ',
  Xi: 'Ξ',
  Pi: 'Π',
  Sigma: 'Σ',
  Upsilon: 'Υ',
  Phi: 'Φ',
  Psi: 'Ψ',
  Omega: 'Ω',
}

/** 运算符映射（LaTeX 命令 → Unicode 字符） */
const OPERATORS: Record<string, string> = {
  times: '×',
  div: '÷',
  pm: '±',
  mp: '∓',
  cdot: '·',
  ast: '∗',
  leq: '≤',
  geq: '≥',
  neq: '≠',
  approx: '≈',
  equiv: '≡',
  sim: '∼',
  infty: '∞',
  partial: '∂',
  nabla: '∇',
  emptyset: '∅',
  to: '→',
  gets: '←',
  in: '∈',
  notin: '∉',
  subset: '⊂',
  supset: '⊃',
  cup: '∪',
  cap: '∩',
  forall: '∀',
  exists: '∃',
  neg: '¬',
  land: '∧',
  lor: '∨',
  ldots: '…',
  cdots: '⋯',
  vdots: '⋮',
  prime: '′',
  leftarrow: '←',
  rightarrow: '→',
  leftrightarrow: '↔',
  sum: '∑',
  int: '∫',
  prod: '∏',
}

type Tok =
  | { kind: 'text'; text: string }
  | { kind: 'open' }
  | { kind: 'close' }
  | { kind: 'sup' }
  | { kind: 'sub' }
  | { kind: 'cmd'; name: string }

type Node =
  | { type: 'text'; text: string }
  | { type: 'group'; children: Node[] }
  | { type: 'sup'; body: Node }
  | { type: 'sub'; body: Node }
  | { type: 'frac'; num: Node[]; den: Node[] }
  | { type: 'sqrt'; body: Node[]; index: Node[] | null }
  | { type: 'op'; symbol: string; upper: Node | null; lower: Node | null }

/** LaTeX 子集 tokenizer：`\cmd`、`{}^_` 为结构 token，其余按文本段累积 */
function tokenize(src: string): Tok[] {
  const toks: Tok[] = []
  let buf = ''
  const flush = (): void => {
    if (buf) {
      toks.push({ kind: 'text', text: buf })
      buf = ''
    }
  }
  let i = 0
  while (i < src.length) {
    const ch = src[i]!
    if (ch === '\\') {
      const m = /^\\[a-zA-Z]+/.exec(src.slice(i))
      if (m) {
        flush()
        toks.push({ kind: 'cmd', name: m[0].slice(1) })
        i += m[0].length
        continue
      }
      // 非字母转义（如 `\ ` 或 `\\`）：字面保留反斜杠，继续累积后续字符
      buf += ch
      i++
      continue
    }
    if (ch === '{' || ch === '}' || ch === '^' || ch === '_') {
      flush()
      toks.push({ kind: ch === '{' ? 'open' : ch === '}' ? 'close' : ch === '^' ? 'sup' : 'sub' })
      i++
      continue
    }
    buf += ch
    i++
  }
  flush()
  return toks
}

/**
 * 解析一组节点，遇到 `}`（close）停止并停留在 close token（由调用方消费）。
 */
function parseGroup(toks: Tok[], pos: { i: number }): Node[] {
  const nodes: Node[] = []
  while (pos.i < toks.length) {
    const tok = toks[pos.i]!
    if (tok.kind === 'close') break
    if (tok.kind === 'open') {
      pos.i++
      const children = parseGroup(toks, pos)
      if (toks[pos.i]?.kind === 'close') pos.i++
      nodes.push({ type: 'group', children })
      continue
    }
    if (tok.kind === 'sup') {
      pos.i++
      nodes.push({ type: 'sup', body: parseAtom(toks, pos) })
      continue
    }
    if (tok.kind === 'sub') {
      pos.i++
      nodes.push({ type: 'sub', body: parseAtom(toks, pos) })
      continue
    }
    if (tok.kind === 'cmd') {
      pos.i++
      nodes.push(parseCommand(toks, pos, tok.name))
      continue
    }
    nodes.push({ type: 'text', text: tok.text })
    pos.i++
  }
  return nodes
}

/** 解析单个原子（sup/sub 的 body，或 group 内元素） */
function parseAtom(toks: Tok[], pos: { i: number }): Node {
  const tok = toks[pos.i]
  if (!tok) return { type: 'text', text: '' }
  if (tok.kind === 'open') {
    pos.i++
    const children = parseGroup(toks, pos)
    if (toks[pos.i]?.kind === 'close') pos.i++
    return { type: 'group', children }
  }
  if (tok.kind === 'cmd') {
    pos.i++
    return parseCommand(toks, pos, tok.name)
  }
  if (tok.kind === 'text') {
    pos.i++
    return { type: 'text', text: tok.text }
  }
  pos.i++
  return { type: 'text', text: '' }
}

/** 解析 `{...}` 分组；缺省返回空组 */
function parseBrace(toks: Tok[], pos: { i: number }): Node[] {
  if (toks[pos.i]?.kind === 'open') {
    pos.i++
    const children = parseGroup(toks, pos)
    if (toks[pos.i]?.kind === 'close') pos.i++
    return children
  }
  return []
}

/** 解析 LaTeX 命令（frac/sqrt/sum/int/希腊字母/运算符/未知字面） */
function parseCommand(toks: Tok[], pos: { i: number }, name: string): Node {
  if (name === 'frac') {
    const num = parseBrace(toks, pos)
    const den = parseBrace(toks, pos)
    return { type: 'frac', num, den }
  }
  if (name === 'sqrt') {
    let index: Node[] | null = null
    const next = toks[pos.i]
    // 可选根指数 \sqrt[n]{x}：`[n]` 落在单个 text token 内
    if (next?.kind === 'text') {
      const m = /^\[([^\]]*)\]/.exec(next.text)
      if (m) {
        index = [{ type: 'text', text: m[1]! }]
        next.text = next.text.slice(m[0].length)
        if (next.text === '') pos.i++
      }
    }
    const body = parseBrace(toks, pos)
    return { type: 'sqrt', body, index }
  }
  if (name === 'sum' || name === 'int' || name === 'prod') {
    const node: Node = {
      type: 'op',
      symbol: name === 'sum' ? '∑' : name === 'int' ? '∫' : '∏',
      upper: null,
      lower: null,
    }
    // 吸收后续 _{...} / ^{...} 作为上下限
    for (;;) {
      const t = toks[pos.i]
      if (t?.kind === 'sub') {
        pos.i++
        node.lower = parseAtom(toks, pos)
        continue
      }
      if (t?.kind === 'sup') {
        pos.i++
        node.upper = parseAtom(toks, pos)
        continue
      }
      break
    }
    return node
  }
  const greek = GREEK[name]
  if (greek) return { type: 'text', text: greek }
  const op = OPERATORS[name]
  if (op) return { type: 'text', text: op }
  // 未知命令：按字面显示，不报错
  return { type: 'text', text: `\\${name}` }
}

function render(n: Node | Node[]): string {
  if (Array.isArray(n)) return n.map(render).join('')
  switch (n.type) {
    case 'text':
      return escapeText(n.text)
    case 'group':
      return n.children.map(render).join('')
    case 'sup':
      return `<span class="sup">${render(n.body)}</span>`
    case 'sub':
      return `<span class="sub">${render(n.body)}</span>`
    case 'frac':
      return `<span class="frac"><span class="num">${render(n.num)}</span><span class="den">${render(n.den)}</span></span>`
    case 'sqrt': {
      const idx = n.index ? `<span class="sqrt-index">${render(n.index)}</span>` : ''
      return `<span class="sqrt">${idx}<span class="radicand">${render(n.body)}</span></span>`
    }
    case 'op': {
      const sup = n.upper ? `<span class="sup">${render(n.upper)}</span>` : ''
      const sub = n.lower ? `<span class="sub">${render(n.lower)}</span>` : ''
      return `<span class="op"><span class="op-sym">${n.symbol}</span><span class="op-limits">${sup}${sub}</span></span>`
    }
    default:
      return ''
  }
}

/**
 * 自研简化 LaTeX 子集渲染（零第三方引擎）。
 *
 * 支持：上标/下标（^ _）、分数 \frac{a}{b}、根号 \sqrt{} / \sqrt[n]{}、
 * 求和/积分/连乘（\sum \int \prod，带 _{...}^{...} 上下限）、希腊字母、常用运算符。
 * 未知命令按字面显示，不报错。
 */
export function renderLatex(code: string): string {
  const toks = tokenize(code)
  const ast = parseGroup(toks, { i: 0 })
  return ast.map(render).join('')
}

const STYLE = `
:host {
  display: inline-block;
  /* 公式是正文内嵌特种文本，跟随外层字号（内部上下标/根号已全 em 相对）；
     定制开口：--oas-equation-font */
  font-size: var(--oas-equation-font, inherit);
  color: var(--oas-color-text-primary);
  line-height: 1.6;
}
:host([hidden]) {
  display: none;
}
/* 块级模式（行间公式）：独立居中行 + 上下间距；字号不放大（display 公式排版惯例） */
:host([display='block']) {
  display: block;
  text-align: center;
  margin-block: var(--oas-equation-block-margin, var(--oas-space-4));
}
:host([display='block']) .equation {
  display: inline-block;
}
/* 外部引擎渲染产物容器：引擎自带样式（如引擎字体/类名）在其自身体内生效 */
.equation[data-engine='injected'] {
  font-style: normal;
}
.equation {
  font-family: 'Times New Roman', 'STIX Two Math', 'Cambria Math', serif;
  font-style: italic;
  white-space: nowrap;
  /* 公式内容恒 LTR：数学排版不做方向镜像（通行做法），RTL 宿主下仅组件布局间距逻辑化，
     公式内部结构（上下标/根号/分式）保持从左到右 */
  direction: ltr;
}
.sup,
.sub {
  font-size: 0.68em;
  font-style: normal;
  line-height: 1;
}
.sup {
  vertical-align: super;
  margin-inline-start: 0.08em;
}
.sub {
  vertical-align: sub;
  margin-inline-start: 0.08em;
}
.frac {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  vertical-align: middle;
  text-align: center;
  margin: 0 0.15em;
}
.frac .num {
  border-bottom: 1px solid currentColor;
  padding: 0 0.3em 0.08em;
}
.frac .den {
  padding: 0.08em 0.3em 0;
}
.sqrt {
  display: inline-flex;
  align-items: flex-start;
  vertical-align: middle;
}
.sqrt::before {
  content: '√';
  font-style: normal;
  margin-inline-end: 0.05em;
}
.sqrt .sqrt-index {
  font-size: 0.6em;
  font-style: normal;
  vertical-align: super;
}
.sqrt .radicand {
  border-top: 1px solid currentColor;
  padding: 0 0.2em;
}
.op {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  vertical-align: middle;
  margin: 0 0.15em;
}
.op .op-sym {
  font-style: normal;
  font-size: 1.35em;
}
.op-limits {
  display: flex;
  align-items: center;
  gap: 0.4em;
}
.op-limits .sup,
.op-limits .sub {
  margin-inline-start: 0;
}
`

/**
 * 可注入的公式渲染引擎协议（薄壳模式的宿主注入对象）：
 * 兼容「renderToString(code, options) → HTML 字符串」形态的 TeX 渲染引擎
 * （如 KaTeX），由宿主自行安装并经 `engine` property 注入。
 *
 * 信任边界：引擎输出作为 innerHTML 直出组件 shadow——注入的引擎是宿主自己的代码，
 * 宿主对其产物负责；SSR 场景引擎需在服务端同样可用（组件 SSR 快照只走属性通道，
 * 引擎属性需宿主在服务端同步注入）。
 */
export interface EquationEngine {
  renderToString(code: string, options?: Record<string, unknown>): string
}

/** 形态校验：注入对象必须携带可调用的 renderToString */
function isEngine(v: unknown): v is EquationEngine {
  return typeof v === 'object' && v !== null && typeof (v as EquationEngine).renderToString === 'function'
}

/**
 * oas-equation —— 数学公式组件（自研简化 LaTeX 子集，零第三方引擎）。
 *
 * 属性（kebab-case）：
 * - `code`：LaTeX 子集源文本
 * - `display`：`'block'` 块级模式（独立居中行间公式），缺省行内
 *
 * Property：
 * - `engine`：注入兼容 TeX 渲染引擎对象（renderToString 协议），注入后委托渲染；
 *   移除（置 null）回到自研子集渲染。非法对象同置 null（丢弃已注入引擎）
 *
 * 渲染为 HTML（span 堆叠 + CSS 排版分数/上下标/根号/求和积分上下限）。
 * 未知命令按字面显示不报错；ARIA：容器 aria-label = 原始 LaTeX。
 * 双轨：自研子集默认（零依赖），引擎注入后薄壳委托（错误静默回退自研）。
 */
export class OASEquation extends OASElement {
  static override get observedAttributes(): string[] {
    return ['code', 'display']
  }

  private injected: EquationEngine | null = null

  /** 注入/移除渲染引擎（property 通道；非法值同置 null——丢弃已注入引擎，回到自研渲染） */
  get engine(): EquationEngine | null {
    return this.injected
  }

  set engine(value: EquationEngine | null) {
    this.injected = isEngine(value) ? value : null
    if (this.isConnected) this.update()
  }

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <div class="equation" part="equation" aria-label=""></div>
    `
  }

  /** 缓存节点引用（render 与水合路径共用；equation 无事件绑定） */
  private bind(): void {}

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（equation 容器存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('[part="equation"]')) return false
    this.bind()
    return true
  }

  protected override update(): void {
    const eq = this.shadow.querySelector<HTMLElement>('[part="equation"]')
    if (!eq) return
    const code = this.getAttr('code', '')
    eq.setAttribute('aria-label', code)
    // 双轨：引擎注入则薄壳委托（错误静默回退自研子集，不空白不抛出）
    if (this.injected) {
      try {
        eq.innerHTML = this.injected.renderToString(code, {
          throwOnError: false,
          displayMode: this.getAttr('display', '') === 'block',
        })
        eq.setAttribute('data-engine', 'injected')
        return
      } catch {
        /* 引擎渲染失败：落到下方自研渲染 */
      }
    }
    eq.removeAttribute('data-engine')
    eq.innerHTML = renderLatex(code)
  }
}
